import { LoadingState, BusyContent, PageHeader } from '../components/ui/Primitives';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import GoogleSignInButton from '../components/GoogleSignInButton';
import PasswordFields from '../components/PasswordFields';
import BillingSettings from '../components/BillingSettings';
import AccountDeletion from '../components/AccountDeletion';
import ContentPreferences from '../components/ContentPreferences';
import { googleAuthRequest } from '../services/googleAuthApi';
import { passwordPolicyError } from '../utils/passwordPolicy';
import './Account.css';

export default function Account() {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const [methods, setMethods] = useState(null);
  const [action, setAction] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (hash === '#account-billing-title') document.getElementById('account-billing-title')?.scrollIntoView({ block: 'start' });
  }, [hash]);

  useEffect(() => {
    let active = true;
    if (!localStorage.getItem('jwt_authorization')) { navigate('/signin', { replace: true }); return; }
    googleAuthRequest('methods', { method: 'GET', authenticated: true })
      .then(result => { if (active) setMethods(result); })
      .catch(failure => { if (active) setError(failure.message); });
    return () => { active = false; };
  }, [navigate]);

  const complete = result => {
    setMethods(result);
    setNotice(action === 'password' ? 'Password added.' : action === 'disconnect' ? 'Google disconnected.' : 'Google connected.');
    setAction(''); setPassword(''); setNewPassword(''); setConfirmation(''); setError('');
  };
  const start = value => { setAction(value); setPassword(''); setError(''); setNotice(''); };
  const disconnect = async event => {
    event.preventDefault();
    setBusy(true); setError('');
    try { complete(await googleAuthRequest('google', { method: 'DELETE', authenticated: true, body: { password } })); }
    catch (failure) { setError(failure.message); }
    finally { setBusy(false); }
  };
  const passwordValid = Boolean(newPassword && newPassword === confirmation && !passwordPolicyError(newPassword));

  return <div className="page-content account-page">
    <PageHeader eyebrow="Your account" title="Account settings" description="Manage your sign-in methods, content preferences, and billing." />
    <section className="account-security-card">
      <h2>Sign-in &amp; security</h2>
      {error ? <p role="alert" className="account-error">{error}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}
      {!methods && !error ? <LoadingState label="Loading your sign-in methods…" /> : null}
      {methods ? <>
        <p className="account-email">{methods.email}</p>
        <div className="account-method-row">
          <div><h2>Google</h2><p>{methods.google.connected ? methods.google.email : 'Not connected'}</p></div>
          {!action && methods.googleAvailable ? <button type="button" className="button" onClick={() => start(methods.google.connected ? 'disconnect' : 'link')}
            disabled={methods.google.connected && !methods.hasPassword}>{methods.google.connected ? 'Disconnect' : 'Connect'}</button> : null}
        </div>
        <div className="account-method-row">
          <div><h2>Password</h2><p>{methods.hasPassword ? 'Password sign-in is available.' : 'You sign in with Google.'}</p></div>
          {methods.hasPassword ? <Link to="/password-reset">Reset password</Link> : !action ? <button type="button" className="button" onClick={() => start('password')}>Set password</button> : null}
        </div>
        {!methods.hasPassword ? <p className="account-hint">Set a password before disconnecting Google.</p> : null}
        {action === 'link' || action === 'disconnect' ? <form className="account-method-form" onSubmit={action === 'disconnect' ? disconnect : event => event.preventDefault()}>
          <label htmlFor="account-current-password">Confirm your Sayable password</label>
          <input id="account-current-password" type="password" autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} required disabled={busy} />
          {action === 'link' ? <GoogleSignInButton purpose="link" disabled={!password} payload={{ password }} onSuccess={complete} />
            : <button className="button" type="submit" disabled={!password || busy}><BusyContent busy={busy} label={"Disconnecting…"}>{'Disconnect Google'}</BusyContent></button>}
        </form> : null}
        {action === 'password' ? <div className="account-method-form">
          <PasswordFields password={newPassword} confirmPassword={confirmation} onPasswordChange={setNewPassword}
            onConfirmPasswordChange={setConfirmation} userInputs={[methods.email]} idPrefix="account-password" />
          <p className="account-hint">Confirm your connected Google account to save your password.</p>
          <GoogleSignInButton purpose="password" disabled={!passwordValid} payload={{ newPassword }} onSuccess={complete} />
        </div> : null}
        {action ? <button type="button" className="account-cancel" onClick={() => start('')} disabled={busy}>Cancel</button> : null}
        <p className="account-hint">Calendar connections are managed separately in <Link to="/integrations">Integrations</Link>.</p>
      </> : null}
    </section>
    <ContentPreferences />
    <BillingSettings />
    <AccountDeletion methods={methods} />
  </div>;
}
